/* eslint-disable */
export enum Os { UNKNOWN, WINDOWS, MACINTOSH, ANDROID, IOS, CHROMEOS }
export enum Browser { UNKNOWN, CHROME, SAFARI, FIREFOX, EDGE_CHROMIUM }

export const osNames = ['unknown', 'windows', 'mac', 'android', 'ios', 'chromeOS'];      // (used in analytics user properties; reconsider before renaming)
export const browserNames = ['unknown', 'chrome', 'safari', 'firefox', 'edgeChromium'];  // (used in analytics user properties; reconsider before renaming)

let utilOs: Os = Os.UNKNOWN;
let utilBrowser: Browser = Browser.UNKNOWN;

export class Utils {
    // OS and browser detected via navigator.userAgent. 
    // https://stackoverflow.com/questions/11219582/how-to-detect-my-browser-version-and-operating-system-using-javascript
    // UA (userAgent) can be tricky to parse, https://developer.mozilla.org/en-US/docs/Web/API/Navigator. https://whatsmyua.info/

    public static getOS(): number {
        if (utilOs !== Os.UNKNOWN) {
            return utilOs;
        }

        utilOs = Os.UNKNOWN;
        if (navigator.userAgent.indexOf('Win') !== -1) { utilOs = Os.WINDOWS; }
        if (navigator.userAgent.indexOf('Mac') !== -1) {
            // Safari on ipadOS 13 returns EXACTLY the same navigator string as Safari on MacOS, 
            // so currently the only way to distinguish ipadOS and MacOS is to see if it is a touch device
            // https://stackoverflow.com/questions/57765958/how-to-detect-ipad-and-ipad-os-version-in-ios-13-and-up
            if (navigator.maxTouchPoints > 1) {
                utilOs = Os.IOS; // iPadOS
            } else {
                utilOs = Os.MACINTOSH;
            }
        }
        if (navigator.userAgent.indexOf('like Mac') !== -1) { utilOs = Os.IOS; } // iOS
        if (navigator.userAgent.indexOf('Android') !== -1) { utilOs = Os.ANDROID; }
        // RegExp: https://github.com/faisalman/ua-parser-js/blob/master/src/ua-parser.js#L686
        if (navigator.userAgent.search(/(cros)\s[\w]+\s([\w\.]+\w)/i) > -1) { utilOs = Os.CHROMEOS; }

        if (utilOs === Os.UNKNOWN) {
            console.log(`Detected Operating System 
                ${['UNKNOWN', 'WINDOWS', 'MACINTOSH', 'ANDROID', 'IOS', 'CHROME OS'][utilOs]} (${utilOs}, UA: ${navigator.userAgent})`);
        }

        return utilOs;
    }

    public static getBrowser(): number {
    
        if (utilBrowser !== Browser.UNKNOWN) {
            return utilBrowser;
        }
    
        utilBrowser = Browser.UNKNOWN;
        
        const userAgent = navigator.userAgent;
        
        // Only allow Chrome and Chromium Edge
        // Check for Chromium Edge first (has both "Chrome" and "Edg")
        if (userAgent.indexOf('Edg') !== -1 && userAgent.indexOf('Chrome') !== -1) {
            utilBrowser = Browser.EDGE_CHROMIUM;
        }
        // Check for Chrome (must not have "Edg" to distinguish from Edge)
        else if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) {
            // Additional check to ensure it's not another Chromium-based browser
            // Chrome should have "Chrome" but not other browser identifiers
            if (userAgent.indexOf('OPR') === -1 &&      // Not Opera
                userAgent.indexOf('Opera') === -1 &&    // Not Opera
                userAgent.indexOf('YaBrowser') === -1 && // Not Yandex
                userAgent.indexOf('Brave') === -1 &&     // Not Brave
                userAgent.indexOf('Vivaldi') === -1) {   // Not Vivaldi
                utilBrowser = Browser.CHROME;
            }
        }
        
        // All other browsers are blocked (Safari, Firefox, non-Chromium Edge, etc.)
        if (utilBrowser === Browser.UNKNOWN) {
            console.log(`Unsupported Browser Detected - Only Chrome and Chromium Edge are supported
                UA: ${userAgent}`);
        }
    
        return utilBrowser;
    }

    public static isTouchDevice(): boolean {
        return localStorage.getItem('isTouchDevice') === 'on';
        // return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
    }

    public static isIos(): boolean {
        return this.getOS() === Os.IOS;
    }

    public static isMacOs(): boolean {
        return this.getOS() === Os.MACINTOSH;
    }

    public static isWindows(): boolean {
        return this.getOS() === Os.WINDOWS;
    }

    public static isChromeOs(): boolean {
        return this.getOS() === Os.CHROMEOS;
    }

    public static isAndroid(): boolean {
        return this.getOS() === Os.ANDROID;
    }

    public static isSafari() {
        return this.getBrowser() === Browser.SAFARI;
    }

    public static enableTextSelection() {
        return !this.isAndroid();
    }

    public static getFormatedTimestamp(): string {
        const now = new Date();
        return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}:${now.getMilliseconds().toString().padStart(3, '0')}`;
    }

    public static getBaseURL() {
        const locationPort = location.port ? `:${location.port}` : '';
        return `${location.protocol}//${location.hostname}${locationPort}`;
    }

    public static loadScript(id: string, path: string) {
        // https://www.ngdevelop.tech/loading-external-libraries-from-cdn-in-angular-application/#dynamically-loading

        return new Promise((resolve, _reject) => {
            if (document.getElementById(id)) {
                // If the script html document element already exist, then resolve promise right away.
                resolve({ id: id, loaded: true, status: 'Already Loaded' });
            }
            else {
                // Load new script in the html document.
                const script = document.createElement('script'); // Create a script element in the html document.
                script.type = 'text/javascript';
                script.src = path;
                script.id = id;

                script.onload = () => resolve({ id: id, loaded: true, status: 'Loaded' });
                script.onerror = (error: any) => resolve({ id: id, loaded: false, status: error });

                document.getElementsByTagName('head')[0].appendChild(script);
            }
        });
    }

    public static isJson(item: string) {
        item = typeof item !== 'string'
            ? JSON.stringify(item)
            : item;

        try {
            item = JSON.parse(item);
        } catch (_) {
            return false;
        }

        if (typeof item === 'object' && item !== null) {
            return true;
        }

        return false;
    }

    public static isValidHttpUrl(string: string) {
        let url;

        try {
            url = new URL(string);
        } catch (_) {
            return false;
        }

        return url.protocol === 'http:' || url.protocol === 'https:';
    }

    public static removeExt(fileName: string) {
        // because modern OS's can handle filenames with multiple '.'s we only remove extension after the last '.' if they are on this list
        const extensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'jpg', 'jpeg', 'png', 'eml', 'rtf', 'msg'];
        const dots = fileName.split('.');
        if (dots) {
            let ext = dots.pop();
            if (ext) {
                ext = ext.toLowerCase();
                if (extensions.includes(ext)) {
                    return dots.join('.');
                }
            }
        }
        return fileName;
    }

    public static delay(timeout: number) {
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                return resolve();
            }, timeout);
        });
    }

    public static objectSize(obj: any) {
        // Estimate object size, https://gist.github.com/zensh/4975495.
        let bytes = 0;

        const sizeOf = (obj) => {
            if (obj !== null && obj !== undefined) {
                switch (typeof obj) {
                    case 'number':
                        bytes += 8;
                        break;
                    case 'string':
                        bytes += obj.length * 2;
                        break;
                    case 'boolean':
                        bytes += 4;
                        break;
                    case 'object':
                        var objClass = Object.prototype.toString.call(obj).slice(8, -1);
                        if (objClass === 'Object' || objClass === 'Array') {
                            for (var key in obj) {
                                if (!obj.hasOwnProperty(key)) continue;
                                sizeOf(obj[key]);
                            }
                        } else bytes += obj.toString().length * 2;
                        break;
                }
            }
            return bytes;
        };
        return sizeOf(obj);
    }

    public static objectSizeOf(obj: any, digits?: number) {
        // Estimate object size, https://gist.github.com/zensh/4975495.

        digits = digits || 1;
        function formatByteSize(bytes) {
            if (bytes < 1024) return bytes + " bytes";
            else if (bytes < 1048576) return (bytes / 1024).toFixed(digits) + " KiB";
            else if (bytes < 1073741824) return (bytes / 1048576).toFixed(digits) + " MiB";
            else return (bytes / 1073741824).toFixed(digits) + " GiB";
        };

        const bytes = this.objectSize(obj);
        return formatByteSize(bytes);
    };

    public static detectChanges(caller: string, changeDetectorRef: any) {
        if (localStorage.getItem('logDetectChanges') === 'on') {
            const start = Date.now();
            changeDetectorRef.detectChanges();
            console.log(this.getFormatedTimestamp(), 'detectChanges()', caller, Date.now() - start, 'ms');
        } else {
            changeDetectorRef.detectChanges();
        }
    }

    /**
     * Fetches a URL with automatic retries in case of failure.
     *
     * @param {string} url - The URL to fetch.
     * @param {boolean} [gcp=true] - Whether the fetch is to a Google Cloud Platform service. 
     * If true, 4xx and 5xx errors will not trigger a retry. Default true.
     * @param {number} [maxRetries=3] - The maximum number of times to retry the fetch in case of failure. Default 3.
     * @returns {Promise<Response>} A Promise that resolves to the Response object representing the response to the request.
     * @throws Will throw an error if the fetch fails after the specified number of retries. Note that 4xx and 5xx errors
     * are not considered failures, and will not trigger a retry.
     */
    public static async fetchWithRetry(url, gcp = true, maxRetries = 3) {
        for (let i = 0; i < maxRetries; i++) {
            try {
                const response = await fetch(url);
                if (!gcp && !response.ok) { // Check if HTTP status is 2xx
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response;
            } catch (error) {
                if (i === maxRetries - 1) { // If last retry, throw error
                    throw error;
                }
                // Awaited: without it the backoff was computed and thrown away, so all three attempts went
                // out back to back and the delay this line exists for never happened.
                await this.delay(100 * (i + 1));
                console.error(`Fetch failed, retrying... (${i + 1}/${maxRetries})`, error);
            }
        }
        return null;
    }

    public static mmToPt(mm: number): number {
        return mm * 2.83465; // 1 mm is approximately 2.83465 points
    }

    public static ptToMm(pt: number): number {
        return pt / 2.83465;
    }

    public static openJsonInNewTab(jsonObject: any) {
        if (jsonObject) {
            const jsonString = JSON.stringify(jsonObject, null, 2); // Convert JSON object to string
            const blob = new Blob([jsonString], { type: 'application/json' }); // Create a Blob from the string
            const url = URL.createObjectURL(blob); // Create a URL for the Blob

            window.open(url, '_blank'); // Open the Blob URL in a new tab
        } else {
            console.error('Invalid JSON object:', jsonObject);
        }
    }

    public static isValidDate(dateString: string): boolean {
        // Define regular expressions for each date format with relaxed separators
        const regexes = [
            /^(?<year>\d{4})[.\-\/\s](?<month>\d{1,2})[.\-\/\s](?<day>\d{1,2})$/, // yyyy-MM-dd
            /^(?<day>\d{1,2})[.\-\/\s](?<month>\d{1,2})[.\-\/\s](?<year>\d{4})$/, // dd.MM.yyyy
            /^(?<month>\d{1,2})[.\-\/\s](?<day>\d{1,2})[.\-\/\s](?<year>\d{4})$/  // MM/dd/yyyy
        ];

        for (const regex of regexes) {
            const match = dateString.match(regex);
            if (match && match.groups) {
                const { year, month, day } = match.groups;
                const date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
                if (date.getFullYear() === parseInt(year) &&
                    date.getMonth() + 1 === parseInt(month) &&
                    date.getDate() === parseInt(day)) {
                    return true;
                }
            }
        }
        return false;
    }
    // Function to format date as YYYY-MM-DD HH:mm:ss
    public static formatDateToISO8601(date: Date): string {
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        const hours = (`0${date.getHours()}`).slice(-2);
        const minutes = (`0${date.getMinutes()}`).slice(-2);
        const seconds = (`0${date.getSeconds()}`).slice(-2);
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    static sanitizeOutputFileName(name: string): string {
        // NFC normalize (macOS uses NFD internally)
        let sanitized = name.normalize('NFC');
        // Remove characters forbidden by Windows/macOS AND minretssag.dk (typographic quotes, apostrophes)
        const invalidCharsRegex = /[<>:"/\\|?*\x00-\x1F\u2018\u2019\u201A\u201B\u2032\u0060\u00B4'\u201C\u201D\u201E\u201F\u2033]/g;
        sanitized = sanitized.replace(invalidCharsRegex, '').trim();
        // Collapse multiple spaces
        sanitized = sanitized.replace(/\s+/g, ' ');
        // Strip leading/trailing periods (trailing causes ..pdf when extension is appended)
        sanitized = sanitized.replace(/^\.+|\.+$/g, '');
        // Fix double (or more) periods before extension
        sanitized = sanitized.replace(/\.{2,}(\w+)$/, '.$1');
        return sanitized;
    }

    // Human-friendly filename timestamp, e.g. "2026-07-29 14.30" (colon is not filesystem-safe).
    static fileNameTimestamp(date: Date = new Date()): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}.${pad(date.getMinutes())}`;
    }

    // Load an image URL (e.g. an enterprise logo/stamp) and return it as a PNG data URL plus its
    // natural pixel size. Used by the annotation report generator to embed the enterprise logo.
    static async convertImageToBase64(url: string): Promise<{ dataURL: string, width: number, height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Enable CORS
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                try {
                    resolve({ dataURL: canvas.toDataURL('image/png'), width: img.width, height: img.height });
                } catch (error) {
                    reject(error);
                }
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = url;
        });
    }
}
